
import { Component, Inject, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import ViewCell from "@/components/views/ViewCell.vue";
import { ViewCellObject, ViewMode, ViewObject, ViewPage, ViewsState } from "@/store/views/types.ts";
import VueGridLayout from "@sureview/vue-grid";
import { get } from "lodash";
import ViewsEventBus from "@/services/views/viewsEventBus";
import VuePerfectScrollbar from "vue-perfect-scrollbar"

const ViewState = namespace("views");

@Component({
	components: {
		"grid-layout": VueGridLayout.GridLayout,
		"grid-item": VueGridLayout.GridItem,
		"view-cell": ViewCell, 
		"scrollbar": VuePerfectScrollbar
	}
})
export default class ViewController extends Vue {

	@Prop({ type: Boolean, default: false }) private mobile: boolean;

	@ViewState.Action deleteView: any;

	@ViewState.Mutation private setSelectedViewCells: (viewCells: ViewCellObject[]) => void;
	@ViewState.Mutation setSelectedTileType: any;

	@ViewState.Getter("getSelectedView") selectedView: ViewObject;
	@ViewState.Getter getSelectedTileType: any;
	@ViewState.Getter getViewMode: ViewMode;

	@ViewState.State((state: ViewsState) => state.selectedViewPageNumber) private selectedViewPageNumber: number;

	@Prop({ type: Number, default: 0 }) private gridHeight: number;
	@Prop({ type: Number, default: 0 }) private gridWidth: number;

	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	private viewCells: ViewCellObject[] = [];
	private cellToAdd: Object = null;
	private transientCellId: number = -Math.floor(100000 + Math.random() * 900000);
	private cellIdToFullscreen: number = 0;

	// unique value for gridlayout to pickup on when update ...
	// because of watcher detecting change to selected tile type
	// causing the grid layout to remove items unpredicably with
	// multi window views when tileType is set to null
	private noTileTypeId : number = -997853215;

	$refs!: {
		mobileCellsContainer: HTMLDivElement;
	};

	/** Gets the (rounded) height of each row in pixels */
	public get scaledRowHeight(): number {
		return Math.round(this.gridHeight / (this.selectedViewPage.rows || 20));
	}

	/**
	 * Gets the (rounded) width of each column in pixels.
	 */
	public get scaledColumnWidth(): number {
		return Math.round(this.gridWidth / (this.selectedViewPage.columns || 5));
	}

	/**
	 * Gets the (un-rounded) width of each column in pixels.
	 */
	public get columnWidth(): number {
		return this.gridWidth / (this.selectedViewPage.columns || 5);
	}

	/**
	 * Returns true if the user is currently editing the View.
	 */
	public get editing() {
		return this.getViewMode === ViewMode.Edit;
	}

	/**
	 * Generates shortcuts for selecting a view cell via the keyboard (Alt + 0-9).
	 */
	public get viewsShortcuts() {
		// Sort cells by their position within the columns/rows (horizontal -> vertically),
		// taking only the first 10.
		let viewCells = [...this.viewCells];
		let cells = viewCells
			.sort((cell1, cell2) => {
				if (cell1.y < cell2.y) {
					return -1;
				} else if (cell1.y > cell2.y) {
					return 1;
				}

				if (cell1.x < cell2.x) {
					return -1;
				} else if (cell1.x > cell2.x) {
					return 1;
				}

				return 0;
			})
			.slice(0, 9);

		let shortcuts = {};

		// Map the cells to shortcuts beginning with Alt
		cells.forEach((cell, idx) => {
			shortcuts[`cell-${cell.viewCellId}`] = ["alt", `${idx + 1}`];
		});

		return shortcuts;
	}
	
	private get selectedViewPage(): ViewPage {
		// Default viewpage to empty incase the selected view page has not loaded yet.
		const defaultViewPage : ViewPage = {
			viewId: 0,
			pageNumber: 0,
			columns: 0,
			rows: 0,
			cells: []
		};

		return this.selectedView && this.selectedView.pages ? this.selectedView.pages.find(p => p.pageNumber === this.selectedViewPageNumber) : defaultViewPage;
	}
	
	private get viewCellsForCurrentPageOnly(): ViewCellObject[] {
		return this.viewCells.filter(c => c.pageNumber === this.selectedViewPageNumber);
	}

	private set viewCellsForCurrentPageOnly(cells: ViewCellObject[]) {
		//cells without pages must be the ones that have been added from @sureview/vue-grid
		const cellsWithoutPages: ViewCellObject[] = cells.filter(cell => !cell.pageNumber);
		cellsWithoutPages.forEach(cell => (cell.pageNumber = this.selectedViewPageNumber));

		//add this page's cells to the main array
		this.viewCells = this.viewCells.filter(cell => cell.pageNumber !== this.selectedViewPageNumber).concat(cells);
	}

	/**
	 * Handles the press of a views shortcut.
	 */
	public handleShortcut(event) {
		let key = event.srcKey as string;

		// If the key starts with a cell value, then we're selecting
		// a view cell.
		if (key.startsWith("cell-")) {
			let cellId = parseInt(key.split("cell-")[1]);

			// Let the cell know it's been selected
			this.eventBus.$emit("focus-set", { viewCellId: cellId });
		}
	}

	public created() {
		if (this.mobile === false) {
			this.eventBus.$on("maximise", this.toggleFullscreen);
		}

		window.addEventListener("keydown", (event) => {
			if(this.selectedView.locked){
				event.stopPropagation()
			}
		}, true);

		window.addEventListener("orientationchange", event => {	
			this.$forceUpdate();	
		});		
	}
	
	public mounted() {
		if (this.selectedView) {
			this.viewCells = this.selectedView.cells
				.filter(valid => valid.width && valid.height)
				.map(cell => {
					return {
						...cell,
						w: cell.width,
						h: cell.height,
						i: cell.viewCellId
					};
				});
		}
	}

	public beforeDestroy() {
		if (this.mobile === false) {
			this.eventBus.$off("maximise", this.toggleFullscreen);
		}
	}

	/**
	 * Handler for when the user chooses to full-screen a plugin.
	 * If the plugin raising the full-screen event is already full-screen, we
	 * reset cellIdToFullscreen back to 0 and 'minimize' it.
	 */
	public toggleFullscreen(viewCellId: number) {
		if (viewCellId == this.cellIdToFullscreen) {
			this.cellIdToFullscreen = 0;
			return;
		}

		this.cellIdToFullscreen = viewCellId;
	}

	/**
	 * Handler for when the user adds a plugin to the grid.
	 */
	private finishAdd(): void {
		const parsedLayout = this.viewCells.map(cell => {
			return { ...cell, height: cell.h, width: cell.w, viewCellId: cell.i };
		});

		this.transientCellId -= 1;

		this.setSelectedViewCells(parsedLayout);
		this.eventBus.$emit("finishedAdd", this.getSelectedTileType );
	}

	/**
	 * Handler for when the user adds, moves or resizes an existing plugin in the grid.
	 */
	public updateLayout(newLayout: ViewCellObject[]): void {
		const parsedLayout: ViewCellObject[] = newLayout.map(cell => {
			return { ...cell, height: cell.h, width: cell.w, viewCellId: cell.i };
		});
		
		const otherPagesCells: ViewCellObject[] = this.selectedView.cells.filter(c => c.pageNumber !== this.selectedViewPageNumber);
		
		this.setSelectedViewCells(parsedLayout.concat(otherPagesCells));			
	}

	/**
	 * Handler for when the user selects a new plugin type to add,
	 * creates a stub view cell with the selected plugin type and
	 * a temporary ID.
	 */
	@Watch("getSelectedTileType")
	public onSelectedTileTypeChanged(value: any) {
		if (value && value.id) {
			this.cellToAdd = {
				pluginTypeId: value.id,
				viewCellId: this.transientCellId
			};
		} else {
			if (value === this.noTileTypeId ) {
				// cell to add = object with doNothing: true 
				// prevent grid layout from emitting updated-layout when this is the case
				this.cellToAdd = {
					doNothing: true
				}
			}
			else {
				this.cellToAdd = null;
			}
		}
	}

	public nullifyCell() {
		setTimeout(()=> {this.cellToAdd = null; }, 50);
	}

	/**
	 * Handler for when the cells within the current View are changed.
	 * 	- Updates our local copy of the view cells with the new data within
	 * the store.
	 *  - Resets our selectedTileType if we're no longer editing, or the user
	 * has reached their max number of a certain plugin type.
	 */
	@Watch("selectedView.cells")
	public onSelectedViewChanged(value: any) {
		if (value) {
			this.viewCells = value
				.filter(valid => valid.width && valid.height)
				.map(cell => {
					return {
						...cell,
						w: cell.width,
						h: cell.height,
						i: cell.viewCellId
					};
				});

			if (
				this.getSelectedTileType != null &&
				!this.editing &&
				this.getSelectedTileType.maxInstances <=
					this.viewCells.filter(cell => cell.pluginTypeId == this.getSelectedTileType.id).length
			) {
				this.setSelectedTileType(null);
			}
		}
	}

	public getMobileCellWidthHeight() {
		// Check if mobileCellsContainer is null then defailt width to 0 otherwise use clientWidth
		let width = !this.$refs.mobileCellsContainer ? 0 : this.$refs.mobileCellsContainer.clientWidth;
		let aspectRatio1 = 0;
		let aspectRatio2 = 0;

		// Portrait
		if (screen.width < screen.height) {
			aspectRatio1 = 4;
			aspectRatio2 = 3;
		} 
		// Landscape
		else {
			aspectRatio1 = 16;
			aspectRatio2 = 9;
		}

		var ratio = width / aspectRatio1;
		var height = ratio * aspectRatio2;
		return `padding: 0px; margin: 0px; width: ${ width  }px; height: ${ height }px`;
	}
}
